// frontend/src/pages/GeofencesPage.jsx
//
// Módulo de GEOCERCAS: crear dibujando en el mapa, editar, activar/desactivar,
// asociar entidades (vehículos, equipos, usuarios, sensores, receptores, GPS,
// empresa…) y consultar el historial de eventos de entrada/salida/permanencia.
//
// Todo el cálculo geométrico y la detección de eventos viven en el backend
// (`geofence_service`); esta pantalla solo captura y muestra.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Undo as UndoIcon,
  History as HistoryIcon,
  LinkOff as UnlinkIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { api } from '../services/api';
import FleetMapPanel from '../components/fleet/FleetMapPanel';

const WEEK_DAYS = [
  ['active_monday', 'Lun'], ['active_tuesday', 'Mar'], ['active_wednesday', 'Mié'],
  ['active_thursday', 'Jue'], ['active_friday', 'Vie'], ['active_saturday', 'Sáb'],
  ['active_sunday', 'Dom'],
];

const EMPTY_FORM = {
  name: '',
  description: '',
  geometry_type: 'polygon',
  color: '#d32f2f',
  active: true,
  start_time: '',
  end_time: '',
  notify_on_enter: true,
  notify_on_exit: true,
  dwell_minutes: '',
  radius_meters: 500,
  ...Object.fromEntries(WEEK_DAYS.map(([key]) => [key, true])),
};

const GeofencesPage = () => {
  const { t } = useTranslation();

  const [metadata, setMetadata] = useState(null);
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // Dibujo
  const [drawing, setDrawing] = useState(false);
  const [drawnPoints, setDrawnPoints] = useState([]);
  const [circleCenter, setCircleCenter] = useState(null);

  // Diálogos
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [entityDialogOpen, setEntityDialogOpen] = useState(false);
  const [entityType, setEntityType] = useState('vehicle');
  const [entityChoice, setEntityChoice] = useState('');
  const [catalogs, setCatalogs] = useState({ vehicle: [], sensor: [], receiver: [], gps: [], user: [] });

  const [eventsOpen, setEventsOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const selected = useMemo(
    () => geofences.find((g) => g.id === selectedId) || null,
    [geofences, selectedId],
  );

  const canCreate = metadata?.permissions?.['geofences.create'];
  const canUpdate = metadata?.permissions?.['geofences.update'];
  const canDelete = metadata?.permissions?.['geofences.delete'];
  const canAssign = metadata?.permissions?.['geofences.assign_entities'];

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.geofences.getAll();
      setGeofences(res.data?.geofences || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudieron cargar las geocercas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.geofences.getMetadata()
      .then((res) => setMetadata(res.data))
      .catch(() => setMetadata({ entity_types: [], permissions: {} }));
    reload();
  }, [reload]);

  // Catálogos para asociar entidades. Se cargan una sola vez, bajo demanda.
  useEffect(() => {
    if (!entityDialogOpen || catalogs.vehicle.length) return;
    Promise.allSettled([
      api.vehicles.getAll(),
      api.sensors.getAll(),
      api.receivers.getAll(),
      api.gps.getAll(),
      api.users.getAll(),
    ]).then(([vehicles, sensors, receivers, gps, users]) => {
      const pick = (result) => (result.status === 'fulfilled'
        ? (result.value?.data?.items || result.value?.data || [])
        : []);
      setCatalogs({
        vehicle: pick(vehicles),
        sensor: pick(sensors),
        receiver: pick(receivers),
        gps: pick(gps),
        user: pick(users),
      });
    });
  }, [entityDialogOpen, catalogs.vehicle.length]);

  // ── dibujo en el mapa ─────────────────────────────────────────────────────
  const handleMapClick = useCallback((point) => {
    if (!drawing) return;
    if (form.geometry_type === 'circle') {
      setCircleCenter(point);
      setDrawnPoints([point]);
    } else {
      setDrawnPoints((points) => [...points, point]);
    }
  }, [drawing, form.geometry_type]);

  const startDrawing = (geometryType) => {
    setForm({ ...EMPTY_FORM, geometry_type: geometryType });
    setEditingId(null);
    setDrawnPoints([]);
    setCircleCenter(null);
    setDrawing(true);
    toast.info(geometryType === 'circle'
      ? 'Haz clic en el mapa para fijar el centro de la geocerca'
      : 'Haz clic en el mapa para marcar cada vértice (mínimo 3)');
  };

  const cancelDrawing = () => {
    setDrawing(false);
    setDrawnPoints([]);
    setCircleCenter(null);
  };

  const openFormFromDrawing = () => {
    if (form.geometry_type === 'circle' && !circleCenter) {
      toast.error('Marca el centro en el mapa antes de continuar');
      return;
    }
    if (form.geometry_type !== 'circle' && drawnPoints.length < 3) {
      toast.error('Marca al menos 3 vértices en el mapa');
      return;
    }
    setFormOpen(true);
  };

  const openEdit = (fence) => {
    setEditingId(fence.id);
    setForm({
      ...EMPTY_FORM,
      ...fence,
      dwell_minutes: fence.dwell_minutes ?? '',
      start_time: fence.start_time || '',
      end_time: fence.end_time || '',
      color: fence.color || '#d32f2f',
    });
    setDrawnPoints([]);
    setCircleCenter(null);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('La geocerca necesita un nombre');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        dwell_minutes: form.dwell_minutes === '' ? null : Number(form.dwell_minutes),
        start_time: form.start_time || null,
        end_time: form.end_time || null,
      };
      if (!editingId) {
        if (form.geometry_type === 'circle') {
          payload.center_lat = circleCenter.lat;
          payload.center_lng = circleCenter.lng;
          payload.radius_meters = Number(form.radius_meters) || 0;
        } else {
          payload.coordinates = drawnPoints;
        }
        const res = await api.geofences.create(payload);
        toast.success('Geocerca creada');
        setSelectedId(res.data?.id || null);
      } else {
        await api.geofences.update(editingId, payload);
        toast.success('Geocerca actualizada');
      }
      setFormOpen(false);
      cancelDrawing();
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar la geocerca');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fence) => {
    try {
      await api.geofences.remove(fence.id, false);
      toast.success('Geocerca desactivada');
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar la geocerca');
    }
  };

  const handleAddEntity = async () => {
    if (!selected || !entityChoice) return;
    try {
      await api.geofences.addEntity(selected.id, {
        entity_type: entityType,
        entity_id: String(entityChoice),
      });
      toast.success('Entidad asociada');
      setEntityDialogOpen(false);
      setEntityChoice('');
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo asociar la entidad');
    }
  };

  const handleRemoveEntity = async (linkId) => {
    if (!selected) return;
    try {
      await api.geofences.removeEntity(selected.id, linkId);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo quitar la asociación');
    }
  };

  const openEvents = async (fence) => {
    setEventsOpen(true);
    setEventsLoading(true);
    try {
      const res = await api.geofences.getEvents(fence.id, { days: 30 });
      setEvents(res.data?.events || []);
    } catch (_) {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const entityOptions = useMemo(() => {
    const list = catalogs[entityType] || [];
    return list.map((item) => ({
      id: item.id,
      label: item.display_name || item.alias || item.sensor_id || item.mac_address
        || item.device_id || item.full_name || item.email || String(item.id).slice(0, 8),
    }));
  }, [catalogs, entityType]);

  return (
    <Container maxWidth={false} disableGutters sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700} sx={{ flexGrow: 1 }}>
          {t('hardcoded.components_fleet.geocercas')}
        </Typography>
        {canCreate && !drawing && (
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => startDrawing('polygon')}>
              Nueva por polígono
            </Button>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => startDrawing('circle')}>
              Nueva por radio
            </Button>
          </>
        )}
        {drawing && (
          <>
            <Chip color="secondary" label={form.geometry_type === 'circle'
              ? (circleCenter ? 'Centro marcado' : 'Marca el centro en el mapa')
              : `${drawnPoints.length} vértice(s)`} />
            <Button size="small" startIcon={<UndoIcon />}
              onClick={() => setDrawnPoints((p) => p.slice(0, -1))}
              disabled={!drawnPoints.length}>
              Deshacer
            </Button>
            <Button variant="contained" onClick={openFormFromDrawing}>Continuar</Button>
            <Button color="inherit" onClick={cancelDrawing}>Cancelar</Button>
          </>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ maxHeight: 620, overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
            ) : geofences.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Aún no hay geocercas. Crea la primera dibujándola en el mapa.
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {geofences.map((fence) => (
                  <ListItemButton
                    key={fence.id}
                    selected={fence.id === selectedId}
                    onClick={() => setSelectedId(fence.id)}
                    sx={{ borderLeft: '4px solid', borderLeftColor: fence.color || 'primary.main' }}
                  >
                    <ListItemText
                      primary={fence.name}
                      secondary={`${fence.geometry_type} · ${fence.entity_count || 0} entidad(es)${fence.active ? '' : ' · inactiva'}`}
                    />
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Historial de eventos">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEvents(fence); }}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canUpdate && (
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(fence); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Desactivar">
                          <IconButton size="small" color="error"
                            onClick={(e) => { e.stopPropagation(); handleDelete(fence); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>

          {selected && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>{selected.name}</Typography>
              {selected.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selected.description}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="overline" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Entidades asociadas
                </Typography>
                {canAssign && (
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setEntityDialogOpen(true)}>
                    Asociar
                  </Button>
                )}
              </Stack>
              {(selected.entities || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Sin entidades asociadas.</Typography>
              ) : (
                <Stack spacing={0.5}>
                  {selected.entities.map((entity) => (
                    <Stack key={entity.id} direction="row" alignItems="center" spacing={1}>
                      <Chip size="small" label={entity.entity_type_label} />
                      <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                        {entity.label || entity.entity_id}
                      </Typography>
                      {canAssign && (
                        <IconButton size="small" onClick={() => handleRemoveEntity(entity.id)}>
                          <UnlinkIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} md={8}>
          <FleetMapPanel
            height={620}
            markers={[]}
            geofences={geofences.filter((f) => f.active)}
            drawingMode={drawing ? form.geometry_type : null}
            drawnPoints={drawnPoints}
            onMapClick={handleMapClick}
            emptyMessage={null}
          />
        </Grid>
      </Grid>

      {/* Formulario de geocerca */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Editar geocerca' : 'Nueva geocerca'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField label="Nombre" value={form.name} required
              onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <TextField label="Descripción" value={form.description || ''} multiline minRows={2}
              onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="Color" type="color" value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })} sx={{ width: 120 }} />
              {form.geometry_type === 'circle' && (
                <TextField label="Radio (m)" type="number" value={form.radius_meters}
                  onChange={(e) => setForm({ ...form, radius_meters: e.target.value })} />
              )}
              <FormControlLabel
                control={<Switch checked={!!form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
                label="Activa"
              />
            </Stack>

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">Horario de vigilancia</Typography>
            </Divider>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {WEEK_DAYS.map(([key, label]) => (
                <Chip
                  key={key}
                  label={label}
                  size="small"
                  color={form[key] ? 'primary' : 'default'}
                  variant={form[key] ? 'filled' : 'outlined'}
                  onClick={() => setForm({ ...form, [key]: !form[key] })}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Desde (HH:MM)" value={form.start_time}
                placeholder="00:00"
                onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              <TextField label="Hasta (HH:MM)" value={form.end_time}
                placeholder="23:59"
                onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            </Stack>

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">Eventos</Typography>
            </Divider>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
              <FormControlLabel
                control={<Switch checked={!!form.notify_on_enter}
                  onChange={(e) => setForm({ ...form, notify_on_enter: e.target.checked })} />}
                label="Registrar entradas"
              />
              <FormControlLabel
                control={<Switch checked={!!form.notify_on_exit}
                  onChange={(e) => setForm({ ...form, notify_on_exit: e.target.checked })} />}
                label="Registrar salidas"
              />
              <TextField label="Permanencia (min)" type="number" value={form.dwell_minutes}
                onChange={(e) => setForm({ ...form, dwell_minutes: e.target.value })}
                helperText="Vacío = no registrar permanencia" sx={{ width: 190 }} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asociar entidad */}
      <Dialog open={entityDialogOpen} onClose={() => setEntityDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Asociar entidad</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="geofence-entity-type">Tipo</InputLabel>
              <Select
                labelId="geofence-entity-type"
                value={entityType}
                label="Tipo"
                onChange={(e) => { setEntityType(e.target.value); setEntityChoice(''); }}
              >
                {(metadata?.entity_types || []).map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {entityOptions.length > 0 ? (
              <FormControl fullWidth size="small">
                <InputLabel id="geofence-entity-id">Entidad</InputLabel>
                <Select
                  labelId="geofence-entity-id"
                  value={entityChoice}
                  label="Entidad"
                  onChange={(e) => setEntityChoice(e.target.value)}
                >
                  {entityOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              // Tipos sin catálogo local (empresa, equipo, grupo…): id manual.
              <TextField
                label="Identificador"
                value={entityChoice}
                onChange={(e) => setEntityChoice(e.target.value)}
                helperText="Sin catálogo para este tipo: escribe el identificador"
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntityDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddEntity} disabled={!entityChoice}>Asociar</Button>
        </DialogActions>
      </Dialog>

      {/* Historial de eventos */}
      <Dialog open={eventsOpen} onClose={() => setEventsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Historial de eventos (30 días)</DialogTitle>
        <DialogContent dividers>
          {eventsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : events.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Sin eventos registrados.</Typography>
          ) : (
            <Stack spacing={1}>
              {events.map((event) => (
                <Stack key={event.id} direction="row" spacing={1} alignItems="center">
                  <Chip size="small"
                    color={event.event_type === 'enter' ? 'success' : event.event_type === 'exit' ? 'warning' : 'default'}
                    label={event.event_type_label} />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {event.entity_type} · {event.entity_id?.slice(0, 8)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {event.occurred_at ? new Date(event.occurred_at).toLocaleString() : '—'}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventsOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GeofencesPage;
